import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client/react";
import { Button, Heading, SearchInput, Text } from "@athena/ui";
import { UserTable, userColumns, type UserRow } from "@/components/tables/UserTable";
import { ClientsDocument } from "@/graphql/clients.generated";
import { ALUMNI_ROOT, CLIENT_IMPORT } from "@/lib/adminPilot";
import { ContactStatusBadge } from "./components/ContactStatusBadge";
import { KipuSyncControl } from "./components/KipuSyncControl";
import type { ClientRecord } from "./helpers/client";
import { daysSince, fullName, toClientRecord, formatContactTime, formatCalendarDate } from "./helpers/client";
import { useOpenedFromHere } from "./helpers/openedFrom";
import { notYetWelcomed, useSendWelcomeEmails } from "./helpers/useSendWelcomeEmails";

const toUserRow = (client: ClientRecord): UserRow => ({
    id: client.id,
    firstName: client.firstName,
    lastName: client.lastName,
    email: client.email ?? "",
});

/** Whether `client` matches a lowercased search term, by the name or email shown in their row. */
const matchesSearch = (client: ClientRecord, term: string): boolean =>
    fullName(client).toLowerCase().includes(term) || Boolean(client.email?.toLowerCase().includes(term));

/**
 * Everyone in the alumni program — every KIPU person, with or without an athena account — on the
 * shared user table so a person's row looks the same here as in every other roster. The list follows
 * KIPU through the scheduled sync, and "Sync from KIPU" runs it now. The mockups' filters and bulk
 * broadcast are their own cards.
 *
 * The search narrows the list in the browser: `Query.clients` returns the whole program unpaged, so
 * every match is already loaded. Move it to the API, as the outreach queue does, if this ever pages.
 *
 * `UserRow` carries the identity fields only, so the alumni columns read the record back by id.
 */
export function AlumniDirectoryPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const openedFrom = useOpenedFromHere();
    const [
        search,
        setSearch,
    ] = useState<string>("");
    const { sendWelcomeEmails, confirmDialog } = useSendWelcomeEmails();
    const { data, loading, error } = useQuery(ClientsDocument);
    const today = useMemo<Date>(() => new Date(), []);
    const clients = useMemo<ClientRecord[]>(
        () =>
            (data?.clients ?? []).map((client) => ({
                ...toClientRecord(client),
                lastContactedAt: client.lastContactedAt ? String(client.lastContactedAt) : undefined,
                nextFollowUp: client.nextFollowUpDate ?? undefined,
            })),
        [data],
    );
    // Counted from what the API says each client can be sent, never guessed (see .claude/rules/ui.md).
    const toWelcome = notYetWelcomed(clients);
    const searchTerm = search.trim().toLowerCase();
    const visibleClients = searchTerm ? clients.filter((client) => matchesSearch(client, searchTerm)) : clients;
    const byId = new Map<string, ClientRecord>(
        clients.map((client) => [
            client.id,
            client,
        ]),
    );

    const columns = [
        { ...userColumns.identity, header: t("admin.alumni.directory.columnName") },
        userColumns.badge("status", t("admin.alumni.directory.columnStatus"), (row) => {
            const client = byId.get(row.id);
            return client ? <ContactStatusBadge status={client.status} /> : null;
        }),
        userColumns.text("daysPostDischarge", t("admin.alumni.directory.columnDaysPostDischarge"), (row) => {
            const discharged = byId.get(row.id)?.dischargeDate;
            return discharged ? String(daysSince(discharged, today)) : undefined;
        }),
        userColumns.text("lastContact", t("admin.alumni.directory.columnLastContact"), (row) => {
            const last = byId.get(row.id)?.lastContactedAt;
            return last ? formatContactTime(last) : undefined;
        }),
        userColumns.text("nextFollowUp", t("admin.alumni.directory.columnNextFollowUp"), (row) => {
            const next = byId.get(row.id)?.nextFollowUp;
            return next ? formatCalendarDate(next) : undefined;
        }),
    ];

    return (
        <div className="flex flex-col gap-6 px-4 py-6 md:px-10">
            <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <Heading level={3} as="h1">
                        {t("admin.alumni.directory.title")}
                    </Heading>
                    <Text size="sm" muted className="mt-1">
                        {t("admin.alumni.directory.description")}
                    </Text>
                </div>
                <div className="flex flex-wrap items-start gap-3">
                    <Button size="sm" className="w-auto" onClick={() => navigate(CLIENT_IMPORT)}>
                        {t("admin.alumni.directory.importButton")}
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        className="w-auto"
                        disabled={toWelcome.length === 0}
                        title={toWelcome.length === 0 ? t("admin.alumni.welcome.noneToSend") : undefined}
                        onClick={() => void sendWelcomeEmails(toWelcome.map((client) => client.id))}
                    >
                        {t("admin.alumni.welcome.sendAll")}
                        {toWelcome.length > 0 ? ` (${toWelcome.length})` : ""}
                    </Button>
                    <KipuSyncControl />
                </div>
            </header>
            <div className="w-full md:w-80">
                <SearchInput
                    label={t("admin.alumni.directory.searchLabel")}
                    className="bg-card h-10 rounded-full"
                    value={search}
                    onValueChange={setSearch}
                />
            </div>
            <UserTable
                columns={columns}
                rows={visibleClients.map(toUserRow)}
                rowKey={(row) => row.id}
                loading={loading}
                empty={
                    <Text size="sm" muted>
                        {error
                            ? t("admin.alumni.directory.loadFailed")
                            : searchTerm
                              ? t("admin.alumni.directory.noMatch", { query: search.trim() })
                              : t("admin.alumni.directory.empty")}
                    </Text>
                }
                onRowClick={(row) => navigate(`${ALUMNI_ROOT}/${row.id}`, { state: openedFrom })}
            />
            {confirmDialog}
        </div>
    );
}
